import React, { useState } from 'react'
import classNames from 'classnames'
import { Link } from 'react-router-dom'
import { Button } from '../../../../components/Button/Button'
import { Tooltip } from '../../../../components/Tooltip/Tooltip'
import { useAppSelector } from '../../../../store/hooks'
import { selectCrossClientResolversInFlight } from '../../../../store/crossClient/crossClientResults'
import { awaitsResolvers } from '../../../../store/crossClient/utils'
import { CcqCards, type CcqCard, type CcqClientView } from './ccqResultView'
import { CcqAiIndicator } from './CcqAiIndicator'
import { CCQ_AI_AWAITING_CLIENT, CCQ_AI_RUN, ccqAiClientRunning, ccqGuidanceMissingData, ccqGuidanceOffer, type CcqGuidanceLine } from './ccqRunCopy'
import { useCcqAi } from './useCcqAi'
import { useCcqAiGate } from './useCcqAiGate'
import { useCcqRun } from './useCcqRun'
import styles from './CcqRail.module.scss'
import { CCQ_SECONDARY_BUTTON_CLASS } from './ccqClassNames'

interface CcqAiGuidanceProps {
  view: CcqClientView
  // The card the panel was opened from.
  card: CcqCard
}

const GuidanceParagraph: React.FC<{ line: CcqGuidanceLine; clientId: string }> = ({ line, clientId }) => (
  <p className='paragraph-medium fw-normal zocks-text-gray-900 mb-0'>
    {line.lead ? <span className='bold'>{line.lead} </span> : null}
    {line.before}{' '}
    <Link
      to={`/library/manage-contacts/contacts/${clientId}`}
      target='_blank'
      rel='noopener noreferrer'
      className={classNames(styles.inlineLink, 'zocks-text-primary fw-bold')}
    >
      {line.link}
    </Link>{' '}
    {line.after}
  </p>
)

const BLOCK = 'd-flex flex-column align-items-start gap-3 px-4 pb-4 flex-shrink-0'

export const CcqAiGuidance: React.FC<CcqAiGuidanceProps> = ({ view, card }) => {
  const { isChecking, runClient } = useCcqAi()
  const { isBlocked, tooltipTitle, tooltipContent } = useCcqAiGate()
  const { execution } = useCcqRun()
  const inFlight = useAppSelector(selectCrossClientResolversInFlight)
  const [isStarting, setIsStarting] = useState(false)

  if (view.isAiChecking || isChecking(view.resultId)) {
    return (
      <div className={BLOCK} role='status'>
        <span className='d-flex align-items-center gap-2 paragraph-medium fw-normal zocks-text-gray-900'>
          <CcqAiIndicator />
          {ccqAiClientRunning(view.firstName)}
        </span>
      </div>
    )
  }

  if (view.card === CcqCards.MISSING_DATA) {
    return (
      <div className={BLOCK}>
        <GuidanceParagraph line={ccqGuidanceMissingData(view.firstName)} clientId={view.clientId} />
      </div>
    )
  }

  if (view.card !== card) {
    return null
  }

  // A panel restored from the URL can show a client the card lists still leave out.
  const isAwaitingResolvers = awaitsResolvers(execution, view.result, inFlight)

  const handleRun = async () => {
    setIsStarting(true)
    try {
      await runClient(view.result, card)
    } finally {
      setIsStarting(false)
    }
  }

  return (
    <div className={BLOCK}>
      <GuidanceParagraph line={ccqGuidanceOffer(card, view.firstName, 'their')} clientId={view.clientId} />
      <Tooltip
        placement='top'
        title={isAwaitingResolvers ? CCQ_AI_AWAITING_CLIENT : tooltipTitle}
        tooltipContent={isAwaitingResolvers ? undefined : tooltipContent}
      >
        <Button
          variant='header'
          className={classNames(CCQ_SECONDARY_BUTTON_CLASS, 'text-nowrap')}
          onClick={() => void handleRun()}
          loading={isStarting}
          disabled={isBlocked || isAwaitingResolvers}
        >
          {CCQ_AI_RUN}
        </Button>
      </Tooltip>
    </div>
  )
}
